import React, { useState } from 'react';
import styles from '../styles/CreatorFinance.module.scss';
import { jsPDF } from 'jspdf';
import html2canvas from "html2canvas";

interface PayoutRecord {
  id: string;
  date: string;
  amount: number;
  method: string;
  status: 'completed' | 'pending' | 'processing';
}

const payoutHistory: PayoutRecord[] = [
  { id: 'PAY-2024-045', date: 'Feb 1, 2026', amount: 2450.0, method: 'Bank Transfer', status: 'completed' },
  { id: 'PAY-2024-044', date: 'Jan 25, 2026', amount: 3120.5, method: 'Bank Transfer', status: 'completed' },
  { id: 'PAY-2024-043', date: 'Jan 18, 2026', amount: 1875.0, method: 'Bank Transfer', status: 'completed' },
  { id: 'PAY-2024-042', date: 'Jan 11, 2026', amount: 2980.25, method: 'Bank Transfer', status: 'completed' },
  { id: 'PAY-2024-041', date: 'Jan 4, 2026', amount: 2145.0, method: 'Bank Transfer', status: 'completed' },
  { id: 'PAY-2024-040', date: 'Dec 28, 2025', amount: 3456.75, method: 'Bank Transfer', status: 'completed' },
  { id: 'PAY-2024-039', date: 'Dec 21, 2025', amount: 1920.0, method: 'Bank Transfer', status: 'completed' },
  { id: 'PAY-2024-038', date: 'Dec 14, 2025', amount: 2760.5, method: 'Bank Transfer', status: 'completed' },
];

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const DollarIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const TrendingIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const CreditCardIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
    <line x1="1" y1="10" x2="23" y2="10" />
  </svg>
);

const CalendarIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const InfoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const DownloadIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const CreatorFinance = () => {
  const [earlyPayoutRequested, setEarlyPayoutRequested] = useState(false);
const contentRef = React.useRef<HTMLDivElement>(null);
  // useEffect(() => {
  //   const fetchFinanceData = async () => {
  //     const response = await Promise.all([
  //       // api.get('/creators/finance'),
  //       api.get('/creators/orders/recent'),
  //       api.get('/creators/payouts')
  //     ]);
  //     // const stats = await response[0].data;
  //     const recentOrders = await response[0].data;
  //     const payouts = await response[1].data;
  //   };
  //   fetchFinanceData();
  // }, []);

   const handleExport = async () => {
    const element = contentRef.current;
    if (!element) return;

    const canvas = await html2canvas(element);
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const imgWidth = 210; // A4 width in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    pdf.save("export.pdf");
  };
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Finance &amp; Payouts</h1>
        <p className={styles.subtitle}>Manage your earnings and payout schedule</p>
      </div>

      {/* Summary Cards */}
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={`${styles.iconWrapper} ${styles.iconPrimary}`}>
            <DollarIcon />
          </div>
          <div className={styles.summaryContent}>
            <span className={styles.summaryLabel}>Available Balance</span>
            <span className={styles.summaryValue}>$12,450.00</span>
            <div className={styles.summaryMeta}>
              <span className={styles.metaBold}>Ready for payout</span>
              <span className={styles.metaMuted}>Next payout on Feb 15, 2026</span>
            </div>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={`${styles.iconWrapper} ${styles.iconAmber}`}>
            <TrendingIcon />
          </div>
          <div className={styles.summaryContent}>
            <span className={styles.summaryLabel}>Pending Earnings</span>
            <span className={styles.summaryValue}>$3,285.50</span>
            <div className={styles.summaryMeta}>
              <span className={styles.metaBold}>Processing</span>
              <span className={styles.metaMuted}>From 28 orders (7-14 days hold)</span>
            </div>
          </div>
        </div>

        <div className={styles.summaryCard}>
          <div className={`${styles.iconWrapper} ${styles.iconNeutral}`}>
            <CreditCardIcon />
          </div>
          <div className={styles.summaryContent}>
            <span className={styles.summaryLabel}>Total Paid Out</span>
            <span className={styles.summaryValue}>$45,231.00</span>
            <div className={styles.summaryMeta}>
              <span className={styles.metaBold}>All time</span>
              <span className={styles.metaMuted}>Across 18 successful payouts</span>
            </div>
          </div>
        </div>
      </div>

      {/* Next Payout Banner */}
      <div className={styles.nextPayoutBanner}>
        <div className={styles.nextPayoutLeft}>
          <div className={`${styles.iconWrapper} ${styles.iconPrimary}`}>
            <CalendarIcon />
          </div>
          <div className={styles.nextPayoutInfo}>
            <h2 className={styles.nextPayoutTitle}>Next Payout Scheduled</h2>
            <p className={styles.nextPayoutDesc}>
              Your next payout of <strong>$12450.00</strong> will be processed on Feb 15, 2026
            </p>
            <span className={styles.nextPayoutBadge}>6 days remaining</span>
          </div>
        </div>
        <button
          className={`${styles.btn} ${styles.btnPrimary} ${earlyPayoutRequested ? styles.btnSuccess : ''}`}
          onClick={() => setEarlyPayoutRequested(true)}
          disabled={earlyPayoutRequested}
        >
          {earlyPayoutRequested ? 'Request Sent!' : 'Request Early Payout'}
        </button>
      </div>

      {/* Platform Commission */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.infoIcon}>
            <InfoIcon />
          </div>
          <div>
            <h2 className={styles.sectionTitle}>Platform Commission</h2>
            <p className={styles.commissionDesc}>
              Pattern Market charges a <strong>5% platform fee</strong> on each sale, plus a{' '}
              <strong>$0.50 transaction fee</strong>. All displayed amounts are after fees have been deducted.
            </p>
          </div>
        </div>
        <div className={styles.feeGrid}>
          <div className={styles.feeCard}>
            <span className={styles.feeLabel}>Platform Fee</span>
            <span className={styles.feeValue}>5%</span>
          </div>
          <div className={styles.feeCard}>
            <span className={styles.feeLabel}>Transaction Fee</span>
            <span className={styles.feeValue}>$0.50</span>
          </div>
          <div className={styles.feeCard}>
            <span className={styles.feeLabel}>Your Earnings</span>
            <span className={styles.feeValue}>~94.5%</span>
          </div>
        </div>
      </div>

      {/* Payout History */}
      <div className={styles.section} ref={contentRef}>
        <div className={styles.tableHeader}>
          <div>
            <h2 className={styles.sectionTitle}>Payout History</h2>
            <p className={styles.sectionSubtitle}>Your completed and pending payouts</p>
          </div>
          <button className={`${styles.btn} ${styles.btnOutline}`} onClick={handleExport}>
            <DownloadIcon />
            Export
          </button>
        </div>

        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Payout ID</th>
                <th>Date</th>
                <th className={styles.alignRight}>Amount</th>
                <th>Method</th>
                <th className={styles.alignRight}>Status</th>
              </tr>
            </thead>
            <tbody>
              {payoutHistory.map((payout) => (
                <tr key={payout.id} className={styles.tableRow}>
                  <td className={styles.payoutId}>{payout.id}</td>
                  <td className={styles.payoutDate}>{payout.date}</td>
                  <td className={`${styles.payoutAmount} ${styles.alignRight}`}>
                    {formatCurrency(payout.amount)}
                  </td>
                  <td className={styles.payoutMethod}>{payout.method}</td>
                  <td className={styles.alignRight}>
                    <span className={`${styles.badge} ${styles[`badge${payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}`]}`}>
                      {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CreatorFinance;